import { AppSettings } from "@/AppSettings";
import { ConfigurationSourceDataItemValue, ConfigurationSourceDataMap, Metadata, SourceDataItemGroupType, SourceDataPriority, Trial } from "@/api/dtos";
import { useMemo } from "react";
import { ConfigurationType } from ".";


const excludedConfigurationSourceDataItemValueCopyProperties: Partial<Record<keyof ConfigurationSourceDataItemValue, boolean>> = {
    id: true,
    trialId: true,
    configurationSourceDataId: true,
    studyEventOid: true,
    caseReportFormOid: true,
    caseReportFormVersionOid: true,
    itemGroupName: true,
    itemName: true,
    value: true,
    linked: true,
    enteredBy: true,
    enteredDate: true,
    modifiedBy: true,
    modifiedDate: true
}

export const copyConfigurationSourceDataItemValueSettings = (
    source: ConfigurationSourceDataItemValue,
    target: ConfigurationSourceDataItemValue,

) => {
    for (let pName in source) {
        const propertyName = pName as keyof ConfigurationSourceDataItemValue;

        if (!excludedConfigurationSourceDataItemValueCopyProperties[propertyName]) {
            (target[propertyName] as any) = source[propertyName] as any;
        }
    }

    return target;
};

export const createItemValueConfiguration = (
    metadata?: Metadata, 
    trialConfigurationItemValues?: ConfigurationSourceDataMap['itemValues'], 
    configurationItemValues?: ConfigurationSourceDataMap['itemValues'], 
    trial?: Trial,  
    isGlobal?: boolean,
    configurationSourceDataId?: number, 
    studyEventOid?: string,
    caseReportFormOid?: string,
    caseReportFormVersionOid?: string,
    itemGroupName?: string,
    itemName?: string,
    itemValue?: string,
    baseRoute?: string
) => {
        if (!metadata || !metadata.itemValues || !caseReportFormOid || !caseReportFormVersionOid || !itemName || !itemValue) {
            return undefined;
        }

        const itemValueMetadata = itemGroupName ? 
            metadata.itemValues?.[caseReportFormOid]?.[caseReportFormVersionOid]?.[itemGroupName]?.[itemName]?.[itemValue] :
            metadata.itemValues?.[caseReportFormOid]?.[caseReportFormVersionOid]?.[AppSettings.metadata.itemGroupUngroupedKey]?.[itemName]?.[itemValue];

        if (!itemValueMetadata) {
            return undefined;
        }

        const itemValueConfiguration = itemGroupName ?
            configurationItemValues?.[AppSettings.metadata.studyEventNullKey]?.[caseReportFormOid]?.[caseReportFormVersionOid]?.[itemGroupName]?.[itemName]?.[itemValue] :
            configurationItemValues?.[AppSettings.metadata.studyEventNullKey]?.[caseReportFormOid]?.[caseReportFormVersionOid]?.[AppSettings.metadata.itemGroupUngroupedKey]?.[itemName]?.[itemValue];

        const eventItemValueConfiguration = studyEventOid ? 
            itemGroupName ?
                configurationItemValues?.[studyEventOid]?.[caseReportFormOid]?.[caseReportFormVersionOid]?.[itemGroupName]?.[itemName]?.[itemValue] :
                configurationItemValues?.[studyEventOid]?.[caseReportFormOid]?.[caseReportFormVersionOid]?.[AppSettings.metadata.itemGroupUngroupedKey]?.[itemName]?.[itemValue] : 
            undefined;

        const trialItemValueConfiguration = itemGroupName ?
            trialConfigurationItemValues?.[AppSettings.metadata.studyEventNullKey]?.[caseReportFormOid]?.[caseReportFormVersionOid]?.[itemGroupName]?.[itemName]?.[itemValue] :
            trialConfigurationItemValues?.[AppSettings.metadata.studyEventNullKey]?.[caseReportFormOid]?.[caseReportFormVersionOid]?.[AppSettings.metadata.itemGroupUngroupedKey]?.[itemName]?.[itemValue];

        const trialEventItemValueConfiguration = studyEventOid ? 
            itemGroupName ?
                trialConfigurationItemValues?.[studyEventOid]?.[caseReportFormOid]?.[caseReportFormVersionOid]?.[itemGroupName]?.[itemName]?.[itemValue] :
                trialConfigurationItemValues?.[studyEventOid]?.[caseReportFormOid]?.[caseReportFormVersionOid]?.[AppSettings.metadata.itemGroupUngroupedKey]?.[itemName]?.[itemValue] : 
            undefined;

        const dominantConfiguration = eventItemValueConfiguration?.linked === false ? 
            eventItemValueConfiguration : 
            trialEventItemValueConfiguration?.linked === false ?
                trialEventItemValueConfiguration :
                itemValueConfiguration?.linked === false ?
                    itemValueConfiguration :
                    trialItemValueConfiguration;


        const parentRoute = baseRoute ?
            baseRoute :
            `/${trial?.oid}/configuration/source-data/${isGlobal ? 'trial' : configurationSourceDataId ? configurationSourceDataId : 'new'}`;


        return {
            itemValueMetadata: itemValueMetadata,
            itemValueConfiguration: itemValueConfiguration,
            eventItemValueConfiguration: eventItemValueConfiguration,
            trialItemValueConfiguration: trialItemValueConfiguration,
            trialEventItemValueConfiguration: trialEventItemValueConfiguration,
            configuration: dominantConfiguration,

            linked: studyEventOid ?
                eventItemValueConfiguration?.linked !== false :
                itemValueConfiguration?.linked !== false,

            type: eventItemValueConfiguration?.linked === false ?
                ConfigurationType.Event :
                trialEventItemValueConfiguration?.linked === false ?
                    ConfigurationType.TrialEvent :
                    itemValueConfiguration?.linked === false ?
                        ConfigurationType.Form :
                        ConfigurationType.Trial,

            icon: AppSettings.icons.itemValue,

            studyEventOid: studyEventOid,
            caseReportFormOid: itemValueMetadata.caseReportFormOid,
            caseReportFormVersionOid: itemValueMetadata.caseReportFormVersionOid,
            itemGroupName: itemValueMetadata.itemGroupName,
            itemName: itemValueMetadata.itemName,
            itemValue: itemValueMetadata.value,


            displayName: dominantConfiguration?.name ?? itemValueMetadata?.name,

            priority: dominantConfiguration?.priority,

            configurationLink: itemGroupName ?
                studyEventOid ?
                    `${parentRoute}/events/${studyEventOid}/forms/${itemValueMetadata?.caseReportFormOid}/versions/${itemValueMetadata.caseReportFormVersionOid}/groups/${itemValueMetadata.itemGroupName}/items/${itemValueMetadata.itemName}/values/${itemValueMetadata.value}` :
                    `${parentRoute}/forms/${itemValueMetadata?.caseReportFormOid}/versions/${itemValueMetadata.caseReportFormVersionOid}/groups/${itemValueMetadata.itemGroupName}/items/${itemValueMetadata.itemName}/values/${itemValueMetadata.value}` :
                studyEventOid ?
                    `${parentRoute}/events/${studyEventOid}/forms/${itemValueMetadata?.caseReportFormOid}/versions/${itemValueMetadata.caseReportFormVersionOid}/items/${itemValueMetadata.itemName}/values/${itemValueMetadata.value}` :
                    `${parentRoute}/forms/${itemValueMetadata?.caseReportFormOid}/versions/${itemValueMetadata.caseReportFormVersionOid}/items/${itemValueMetadata.itemName}/values/${itemValueMetadata.value}`
        };
}

const useItemValueConfiguration = (
    metadata?: Metadata, 
    trialConfiguration?: ConfigurationSourceDataMap, 
    configuration?: ConfigurationSourceDataMap, 
    trial?: Trial,  
    configurationSourceDataId?: number, 
    studyEventOid?: string,
    caseReportFormOid?: string,
    caseReportFormVersionOid?: string,
    itemGroupName?: string,
    itemName?: string,
    itemValue?: string,
    baseRoute?: string
) => {
    return  useMemo(() => {
        return createItemValueConfiguration(
            metadata, 
            trialConfiguration?.itemValues, 
            configuration?.itemValues, 
            trial, 
            configuration?.configuration?.isGlobal,
            configuration?.configuration?.id ?? configurationSourceDataId,  
            studyEventOid,
            caseReportFormOid,
            caseReportFormVersionOid,
            itemGroupName,
            itemName,
            itemValue,
            baseRoute
        );
    }, [metadata, trialConfiguration?.itemValues, configuration?.itemValues, studyEventOid, caseReportFormOid, caseReportFormVersionOid, itemGroupName, itemName, itemValue, trial, configurationSourceDataId, baseRoute]);
};

export default useItemValueConfiguration;